import { Tooltip } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import UrgencyLogo from 'src/assets/logos/urgency.svg';
import { ContactAvatar } from 'src/Components/Contacts/ContactAvatar';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import useSortableRecords from 'src/Hooks/Shared/useSortableRecords';

import styles from './ContactList.module.scss';
import { Props } from './ContactList.types';

//t('contacts.mr', 'M')
//t('contacts.ms', 'Mme')

const ContactList = ({
  contacts,
  selectedContacts,
  setSelectedContacts,
  onContactClick,
  fetching,
  rightColumns,
}: Props) => {
  const isMobile = useIsMobile();
  const isTablet = useIsMobile(1050);
  const { sortedRecords, sortStatus, setSortStatus } = useSortableRecords(contacts);

  const { t } = useTranslation();

  useEffect(() => {
    if (selectedContacts && setSelectedContacts && contacts) {
      if (selectedContacts.length > 0) {
        // Filter out any selectedContacts that no longer exist in contacts
        const updatedSelectedContacts = selectedContacts.filter(selectedContact =>
          contacts.some(contact => contact.id === selectedContact.id)
        );

        // Only update if there's an actual change
        if (updatedSelectedContacts.length !== selectedContacts.length) {
          setSelectedContacts(updatedSelectedContacts);
        }
      }
    }
  }, [contacts, selectedContacts, setSelectedContacts]);

  return (
    <DataTable
      withRowBorders={false}
      style={{ width: '100%', minHeight: '10rem' }}
      records={sortedRecords}
      sortStatus={sortStatus}
      onSortStatusChange={setSortStatus}
      selectedRecords={selectedContacts}
      onSelectedRecordsChange={setSelectedContacts}
      noRecordsText={t('contacts.empty', 'Aucun contacts')}
      fetching={fetching}
      onRowClick={({ record }) => onContactClick(record)}
      columns={[
        {
          accessor: 'name',
          title: t('contacts.table.name', 'Nom'),
          sortable: true,
          render: ({ name, kind, imageUrl, mainInterlocutorLabel, websiteUrl, logoEmails }) => (
            <div className={styles.avatarName}>
              {!isMobile && (
                <ContactAvatar
                  name={name}
                  kind={kind}
                  imageUrl={imageUrl}
                  websiteUrl={websiteUrl}
                  emails={logoEmails}
                  size={38}
                />
              )}
              <div className={styles.nameBlock}>
                <div className={styles.name}>{name}</div>
                {mainInterlocutorLabel && (
                  <div className={styles.secondaryLine}>{mainInterlocutorLabel}</div>
                )}
              </div>
            </div>
          ),
        },
        {
          accessor: 'phoneNumber',
          title: t('contacts.table.phoneNumber', 'Téléphone'),
          sortable: true,
          hidden: isTablet,
          render: ({ phoneNumber, isEmergencyContact }) => (
            <div className={styles.phoneNumberWithEmergency}>
              {phoneNumber && <div className={styles.phoneNumber}>{phoneNumber}</div>}
              {isEmergencyContact && (
                <Tooltip
                  withArrow
                  label={t('contacts.emergencyContactTooltip', "Contact d'urgence")}
                >
                  <img src={UrgencyLogo} className={styles.urgencyLogo} alt={'emergency'} />
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          accessor: 'category',
          title: t('contacts.table.category', 'Catégorie'),
          sortable: true,
          hidden: isTablet,
          render: ({ category }) => (
            <div className={styles.value}>
              {category ? t(`contacts.categories.${category}`, category) : '-'}
            </div>
          ),
        },
        {
          accessor: 'subcategory',
          title: t('contacts.table.subcategory', 'Sous-catégorie'),
          sortable: true,
          hidden: isTablet,
          render: ({ subcategory }) => (
            <div className={styles.value}>
              {subcategory ? t(`contacts.subcategories.${subcategory}`, subcategory) : '-'}
            </div>
          ),
        },
        ...(rightColumns ?? []),
      ]}
    />
  );
};

export default ContactList;
